import * as React from 'react';

import { MAX_QUESTION_ANSWERS } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { SmallButton } from '~shared/components/buttons';
import { IconPlus } from '~shared/components/icons/standard';
import {
  QuestionDetails,
  QuestionPoints,
  QuestionText,
  useSelectQuestion,
} from '~teacher/components/QuizQuestion';
import {
  useHandleChangeWeight,
  useHandleImageChange,
  useHandleQuestionTextChange,
} from '~teacher/components/QuizQuestion/hooks';
import { QuestionMedia } from '~teacher/components/QuizQuestion/QuestionMedia';
import { proCheck } from '~teacher/util/proCheck';

import {
  makeSelectAnswers,
  makeSelectEditAnswers,
  makeSelectIsControlsDisabled,
} from '../../slices/quiz/quizSelectors';
import { addAnswerToQuestion } from '../../slices/quiz/quizSlice';
import MultipleChoiceAnswers from './MultipleChoiceAnswers';

function useHandleAddAnswerToQuestion() {
  const dispatch = useDispatch();
  return () => {
    dispatch(addAnswerToQuestion({}));
  };
}

function setAnswerPlaceholder(index: number) {
  const mcAnswerCode =
    index > 25
      ? 'A' + String.fromCharCode((index % 26) + 65)
      : String.fromCharCode(index + 65);

  const answerPlaceHolder = translate('Answer {0}').format(mcAnswerCode);
  return answerPlaceHolder;
}

interface Props {
  hasCustomGradingWeight?: boolean;
  questionId: string;
  order: number;
  isLocked: boolean;
}

export default function QuestionMultipleChoice({
  questionId,
  hasCustomGradingWeight = false,
  order,
  isLocked,
}: Props) {
  const {
    answers,
    explanation,
    explanationImage,
    gradingWeight,
    questionText,
    type,
    isEditing,
  } = useSelectQuestion({ questionId: questionId });

  const isDisabled = useSelector(makeSelectIsControlsDisabled());

  const handleAddAnswerToQuestion = useHandleAddAnswerToQuestion();
  const handleQuestionTextChange = useHandleQuestionTextChange();
  const handleChangeWeight = useHandleChangeWeight();
  const handleImageChange = useHandleImageChange();

  const editAnswers = useSelector(makeSelectEditAnswers());
  const quizAnswers = useSelector(makeSelectAnswers());

  const showAdvancedQuizEditor = proCheck.canShowAdvancedQuizEditor();

  return (
    <div className="question-grid flex-1" data-question={type}>
      <QuestionText
        isLocked={isLocked}
        questionId={questionId}
        order={order}
        text={questionText}
        weight={gradingWeight}
        placeholder={translate('Have a multiple-choice question to ask?')}
        isEditing={isEditing}
        isDisabled={isDisabled}
        onQuestionTextChange={handleQuestionTextChange}
        onChangeWeight={handleChangeWeight}
        showAdvancedQuizEditor={showAdvancedQuizEditor}
      />
      <div
        className={cx(
          'flex xl:justify-end w-124 h-fit mt-6 -ml-6 sm:ml-15 xl:mt-0 xl:ml-0 lg:row-start-2 xl:row-span-2 xl:col-start-2 xl:text-right',
          {
            'justify-center sm:justify-start': isEditing,
          }
        )}
      >
        <QuestionMedia
          questionId={questionId}
          onChangeImage={handleImageChange}
        />
      </div>
      <div
        className={`xl:row-start-2 lg:row-start-3 ${
          isEditing ? 'mt-4 -mr-12 md:mr-0' : ''
        }`}
      >
        <QuestionPoints
          isHidden={!hasCustomGradingWeight || isEditing}
          weight={gradingWeight}
        />
        {answers.map((answerId, index) => {
          const mcAnswers = isEditing ? editAnswers : quizAnswers;
          if (mcAnswers[answerId]) {
            return (
              <MultipleChoiceAnswers
                answer={mcAnswers[answerId]}
                answerPlaceholder={setAnswerPlaceholder(index)}
                key={answerId}
                isFirst={index === 0}
                isEditing={isEditing}
                isDisabled={isDisabled}
              />
            );
          }
        })}
        {isEditing && (
          <div className="grid grid-cols-question-fields-question-editor max-w-5xl p-0 mt-4">
            <div className="col-start-3 pl-13">
              <SmallButton
                isOnWhite={false}
                onClick={handleAddAnswerToQuestion}
                isDisabled={
                  isDisabled || answers.length >= MAX_QUESTION_ANSWERS
                }
              >
                <span className="inline-flex mr-1 -ml-2 text-inherit">
                  <IconPlus />
                </span>
                {translate('Add Answer')}
              </SmallButton>
            </div>
          </div>
        )}
        <QuestionDetails
          isDisabled={isDisabled}
          questionId={questionId}
          content={explanation}
          image={explanationImage}
          isEditing={isEditing}
        />
      </div>
    </div>
  );
}
